import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  Plus,
  Edit,
  Trash2,
  Clock,
  DollarSign,
  Eye,
  EyeOff,
} from "lucide-react";
import { useStoreManagementStore } from "@/store/storeManagementStore";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { MenuItem, CategoryWithMenuItems } from "@/types/menu";
import {
  fetchMenuItems,
  createMenuItem,
  deleteMenuItem,
} from "@/services/menu";
import {
  fetchMenuCategories,
  createMenuCategory,
  deleteMenuCategory,
} from "@/services/menu";
import { useMenuStore } from "@/store/menuStore";
import { useMenuCategories, useMenuCacheUtils, useMenuItems } from "@/services/menu/menuItem/fetchMenuItems";

export function BeautyServices() {
  const { t } = useTranslation();
  const [isCreateCategoryOpen, setIsCreateCategoryOpen] = useState(false);
  const [isCreateServiceOpen, setIsCreateServiceOpen] = useState(false);
  const { selectedStoreId } = useStoreManagementStore();
  // const [categories, setCategories] = useState<CategoryWithMenuItems[]>([]);
  // const [isLoading, setIsLoading] = useState(false);
  // const [error, setError] = useState<string | null>(null);
  const { data: categories = [], isLoading, error } = useMenuCategories(selectedStoreId);
  const { data: menuItems = [] } = useMenuItems(selectedStoreId);
  const { invalidateCategories } = useMenuCacheUtils();

  // Create categoriesWithItems structure for backward compatibility
  const categoriesWithItems = categories.map(category => ({
    ...category,
    menu_items: menuItems.filter(item => 
      item.categoryIds?.includes(category.id)
    )
  }));

  const [newCategory, setNewCategory] = useState({
    name: "",
    description: "",
    store_id: selectedStoreId || "",
  });
  const [newService, setNewService] = useState({
    name: "",
    description: "",
    price: "0",
    duration_minutes: 30,
    image_url: "",
    categoryIds: [] as string[],
    available: true,
    store_id: selectedStoreId || "",
    tags: [] as string[],
  });

  useEffect(() => {
    if (selectedStoreId) {
      setNewService((prev) => ({ ...prev, store_id: selectedStoreId }));
    }
  }, [selectedStoreId]);

  const handleCreateCategory = async () => {
    if (!selectedStoreId) return;
    try {
      await createMenuCategory(newCategory);
      setNewCategory({ name: "", description: "", store_id: selectedStoreId });
      setIsCreateCategoryOpen(false);
      invalidateCategories(selectedStoreId);
    } catch (err) {
      console.error("Error creating category:", err);
    }
  };

  const handleCreateService = async () => {
    if (!selectedStoreId) return;
    try {
      await createMenuItem(
        {
          ...newService,
          store_id: selectedStoreId,
        },
        newService.categoryIds,
        newService.tags
      );
      setNewService({
        name: "",
        description: "",
        price: "0",
        duration_minutes: 30,
        image_url: "",
        categoryIds: [],
        available: true,
        store_id: selectedStoreId,
        tags: [],
      });
      setIsCreateServiceOpen(false);
      invalidateCategories(selectedStoreId);
    } catch (err) {
      console.error("Error creating service:", err);
    }
  };

  const handleDeleteService = async (id: string) => {
    if (!selectedStoreId) return;
    try {
      await deleteMenuItem(id, selectedStoreId);
      invalidateCategories(selectedStoreId);
    } catch (err) {
      console.error("Error deleting service:", err);
    }
  };

  const handleDeleteCategory = async (id: string) => {
    if (!selectedStoreId) return;
    try {
      await deleteMenuCategory(id, selectedStoreId);
      invalidateCategories(selectedStoreId);
    } catch (err) {
      console.error("Error deleting category:", err);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <CardTitle className="text-destructive">
              {t("beautyServices.error")}
            </CardTitle>
            <CardDescription>{error.message}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                {t("beautyServices.title")}
              </h1>
              <p className="text-muted-foreground mt-1">
                {t("beautyServices.description")}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Categories Management Section */}
        <Card className="shadow-lg border-0">
          <CardHeader className="border-b bg-muted/30">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl">
                  {t("beautyServices.categories.title")}
                </CardTitle>
                <CardDescription>
                  {t("beautyServices.categories.description")}
                </CardDescription>
              </div>
              <Dialog
                open={isCreateCategoryOpen}
                onOpenChange={setIsCreateCategoryOpen}
              >
                <DialogTrigger asChild>
                  <Button className="shadow-md">
                    <Plus className="mr-2 h-4 w-4" />
                    {t("beautyServices.categories.addCategory")}
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px]">
                  <DialogHeader>
                    <DialogTitle>
                      {t("beautyServices.categories.createNewCategory")}
                    </DialogTitle>
                    <DialogDescription>
                      {t(
                        "beautyServices.categories.createNewCategoryDescription"
                      )}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="categoryName">
                        {t("beautyServices.categories.categoryName")}
                      </Label>
                      <Input
                        id="categoryName"
                        value={newCategory.name}
                        onChange={(e) =>
                          setNewCategory({
                            ...newCategory,
                            name: e.target.value,
                          })
                        }
                        placeholder={t(
                          "beautyServices.categories.categoryNamePlaceholder"
                        )}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="categoryDescription">
                        {t("beautyServices.categories.description")}
                      </Label>
                      <Textarea
                        id="categoryDescription"
                        value={newCategory.description}
                        onChange={(e) =>
                          setNewCategory({
                            ...newCategory,
                            description: e.target.value,
                          })
                        }
                        placeholder={t(
                          "beautyServices.categories.descriptionPlaceholder"
                        )}
                      />
                    </div>
                    <div className="flex justify-end space-x-2 pt-4">
                      <Button
                        variant="outline"
                        onClick={() => setIsCreateCategoryOpen(false)}
                      >
                        {t("beautyServices.categories.cancel")}
                      </Button>
                      <Button onClick={handleCreateCategory}>
                        {t("beautyServices.categories.createCategory")}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      {t("beautyServices.categories.table.name")}
                    </TableHead>
                    <TableHead>
                      {t("beautyServices.categories.table.description")}
                    </TableHead>
                    <TableHead>
                      {t("beautyServices.categories.table.services")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("beautyServices.categories.table.actions")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categories.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-12">
                        <div className="text-muted-foreground">
                          <p className="text-lg font-medium">
                            {t("beautyServices.categories.noCategories")}
                          </p>
                          <p className="text-sm">
                            {t(
                              "beautyServices.categories.noCategoriesDescription"
                            )}
                          </p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    categories.map((category) => (
                      <TableRow key={category.id}>
                        <TableCell className="font-medium">
                          {category.name}
                        </TableCell>
                        <TableCell>{category.description}</TableCell>
                        <TableCell>
                          {
                            categoriesWithItems.find(
                              (c) => c.id === category.id
                            )?.menu_items.length
                          }
                          services
                        </TableCell>
                        <TableCell className="text-right">
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  {t(
                                    "beautyServices.categories.deleteCategory"
                                  )}
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  {t(
                                    "beautyServices.categories.deleteCategoryDescription"
                                  )}
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>
                                  {t("beautyServices.categories.cancel")}
                                </AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() =>
                                    handleDeleteCategory(category.id)
                                  }
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  {t("beautyServices.categories.delete")}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Services Management Section */}
        <Card className="shadow-lg border-0">
          <CardHeader className="border-b bg-muted/30">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl">
                  {t("beautyServices.services.title")}
                </CardTitle>
                <CardDescription>
                  {t("beautyServices.services.description")}
                </CardDescription>
              </div>
              <Dialog
                open={isCreateServiceOpen}
                onOpenChange={setIsCreateServiceOpen}
              >
                <DialogTrigger asChild>
                  <Button className="shadow-md">
                    <Plus className="mr-2 h-4 w-4" />
                    {t("beautyServices.services.addService")}
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[600px]">
                  <DialogHeader>
                    <DialogTitle>
                      {t("beautyServices.services.createNewService")}
                    </DialogTitle>
                    <DialogDescription>
                      {t("beautyServices.services.createNewServiceDescription")}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="space-y-2">
                      <Label>{t("beautyServices.services.serviceName")}</Label>
                      <Input
                        value={newService.name}
                        onChange={(e) =>
                          setNewService({ ...newService, name: e.target.value })
                        }
                        placeholder={t(
                          "beautyServices.services.serviceNamePlaceholder"
                        )}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>{t("beautyServices.services.description")}</Label>
                      <Textarea
                        value={newService.description}
                        onChange={(e) =>
                          setNewService({
                            ...newService,
                            description: e.target.value,
                          })
                        }
                        placeholder={t(
                          "beautyServices.services.descriptionPlaceholder"
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{t("beautyServices.services.price")}</Label>
                        <Input
                          type="number"
                          value={newService.price}
                          onChange={(e) =>
                            setNewService({
                              ...newService,
                              price: e.target.value,
                            })
                          }
                          placeholder="0.00"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t("beautyServices.services.duration")}</Label>
                        <Input
                          type="number"
                          value={newService.duration_minutes}
                          onChange={(e) =>
                            setNewService({
                              ...newService,
                              duration_minutes: parseInt(e.target.value),
                            })
                          }
                          placeholder="30"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>{t("beautyServices.services.imageUrl")}</Label>
                      <Input
                        value={newService.image_url}
                        onChange={(e) =>
                          setNewService({
                            ...newService,
                            image_url: e.target.value,
                          })
                        }
                        placeholder={t(
                          "beautyServices.services.imageUrlPlaceholder"
                        )}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>{t("beautyServices.services.category")}</Label>
                      <Select
                        value={newService.categoryIds[0] || ""}
                        onValueChange={(value) =>
                          setNewService({ ...newService, categoryIds: [value] })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={t(
                              "beautyServices.services.selectCategory"
                            )}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((category) => (
                            <SelectItem key={category.id} value={category.id}>
                              {category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex justify-end space-x-2 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => setIsCreateServiceOpen(false)}
                    >
                      {t("beautyServices.services.cancel")}
                    </Button>
                    <Button onClick={handleCreateService}>
                      {t("beautyServices.services.createService")}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      {t("beautyServices.services.table.service")}
                    </TableHead>
                    <TableHead>
                      {t("beautyServices.services.table.category")}
                    </TableHead>
                    <TableHead>
                      {t("beautyServices.services.table.duration")}
                    </TableHead>
                    <TableHead>
                      {t("beautyServices.services.table.price")}
                    </TableHead>
                    <TableHead>
                      {t("beautyServices.services.table.status")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("beautyServices.services.table.actions")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categories.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-12">
                        <div className="text-muted-foreground">
                          <p className="text-lg font-medium">
                            {t("beautyServices.services.noServices")}
                          </p>
                          <p className="text-sm">
                            {t("beautyServices.services.noServicesDescription")}
                          </p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    categoriesWithItems.flatMap((category) =>
                      category.menu_items.map((service) => (
                        <TableRow
                          key={service.id}
                          className="hover:bg-muted/50"
                        >
                          <TableCell className="w-[300px]">
                            <div className="flex items-center space-x-3">
                              <Avatar className="h-10 w-10">
                                <AvatarImage
                                  src={service.image_url || ""}
                                  alt={service.name}
                                />
                                <AvatarFallback className="bg-primary/10 text-primary">
                                  {service.name.substring(0, 2).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <div className="font-medium">
                                  {service.name}
                                </div>
                                <div className="text-sm text-muted-foreground line-clamp-1">
                                  {service.description}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{category.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="font-mono">
                              <Clock className="mr-1 h-3 w-3" />
                              {service.duration_minutes}m
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="font-mono">
                              <DollarSign className="mr-1 h-3 w-3" />
                              {typeof service.price === "string"
                                ? parseFloat(service.price).toFixed(2)
                                : service.price.toFixed(2)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                service.available ? "default" : "secondary"
                              }
                            >
                              {service.available ? (
                                <Eye className="mr-1 h-3 w-3" />
                              ) : (
                                <EyeOff className="mr-1 h-3 w-3" />
                              )}
                              {service.available
                                ? t("beautyServices.services.available")
                                : t("beautyServices.services.hidden")}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-destructive"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>
                                    {t("beautyServices.services.deleteService")}
                                  </AlertDialogTitle>
                                  <AlertDialogDescription>
                                    {t(
                                      "beautyServices.services.deleteServiceDescription"
                                    )}
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() =>
                                      handleDeleteService(service.id)
                                    }
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  >
                                    {t("beautyServices.services.delete")}
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </TableCell>
                        </TableRow>
                      ))
                    )
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
